// Persist gems/XP/streak/hearts to Supabase when available; localStorage otherwise.
// Keeps the demo experience working even before Supabase is configured.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { useAuth } from "../context/AuthContext.jsx";

const DEFAULT = { gems: 120, hearts: 5, streak: 7, xp: 340, level: 3 };
const LS_KEY = "lc_stats";

function loadLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? { ...DEFAULT, ...JSON.parse(raw) } : { ...DEFAULT };
  } catch {
    return { ...DEFAULT };
  }
}

export function useUserStats() {
  const { user, session } = useAuth();
  const [stats, setStatsInternal] = useState(loadLocal);

  // Hydrate from Supabase on login.
  useEffect(() => {
    if (!supabase || !session?.user?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("user_stats")
        .select("gems, xp, level, streak_days, hearts")
        .eq("user_id", session.user.id)
        .maybeSingle();
      if (cancelled || !data) return;
      setStatsInternal({
        gems: data.gems,
        xp: data.xp,
        level: data.level,
        streak: data.streak_days,
        hearts: data.hearts,
      });
    })();
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  const save = useCallback(async (next) => {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    if (!supabase || !session?.user?.id) return;
    await supabase
      .from("user_stats")
      .upsert({
        user_id: session.user.id,
        gems: next.gems,
        xp: next.xp,
        level: next.level,
        streak_days: next.streak,
        hearts: next.hearts,
      }, { onConflict: "user_id" });
  }, [session?.user?.id]);

  const setStats = useCallback((updater) => {
    setStatsInternal((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      save(next);
      return next;
    });
  }, [save]);

  // Also persist when user signs in as guest (localStorage already handled).
  useEffect(() => { if (!user) setStatsInternal(DEFAULT); }, [user]);

  return [stats, setStats];
}
